import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { ArrowLeft, Download, FileText, Lock } from 'lucide-react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

const BAR_COLORS = ['#dc2626','#f59e0b','#10b981','#6366f1','#ec4899','#06b6d4'];

const BarChart = ({ data, maxVal }) => (
  <div className="flex items-end gap-3 h-52 mt-4 px-2">
    {data.map((d, i) => (
      <div key={i} className="flex-1 flex flex-col items-center gap-1 h-full justify-end">
        <span className="text-xs text-gray-400 font-mono">{d.value}</span>
        <div
          className="w-full rounded-t transition-all duration-700"
          style={{ height: `${Math.max(5, (d.value / maxVal) * 100)}%`, backgroundColor: BAR_COLORS[i % BAR_COLORS.length] }}
        />
        <span className="text-[10px] text-gray-500 text-center leading-tight mt-1">{d.label}</span>
      </div>
    ))}
  </div>
);

const LineChart = ({ points }) => {
  const max = Math.max(...points.map(p => p.y));
  const w = 500, h = 160, pad = 30;
  const coords = points.map((p, i) => ({
    x: pad + (i / (points.length - 1)) * (w - 2 * pad),
    y: h - pad - ((p.y / max) * (h - 2 * pad))
  }));
  const line = coords.map((c, i) => `${i === 0 ? 'M' : 'L'}${c.x},${c.y}`).join(' ');
  const area = `${line} L${coords[coords.length-1].x},${h-pad} L${coords[0].x},${h-pad} Z`;

  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full mt-4">
      <defs>
        <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#dc2626" stopOpacity="0.3"/>
          <stop offset="100%" stopColor="#dc2626" stopOpacity="0"/>
        </linearGradient>
      </defs>
      <line x1={pad} y1={h-pad} x2={w-pad} y2={h-pad} stroke="#333" strokeWidth="1"/>
      <line x1={pad} y1={pad} x2={pad} y2={h-pad} stroke="#333" strokeWidth="1"/>
      <path d={area} fill="url(#areaGrad)"/>
      <path d={line} fill="none" stroke="#dc2626" strokeWidth="2.5"/>
      {coords.map((c, i) => (
        <g key={i}>
          <circle cx={c.x} cy={c.y} r="4" fill="#dc2626" stroke="#0a0a0a" strokeWidth="2"/>
          <text x={c.x} y={h - 10} textAnchor="middle" fill="#666" fontSize="10">{points[i].label}</text>
          <text x={c.x} y={c.y - 10} textAnchor="middle" fill="#aaa" fontSize="9">{points[i].y}M$</text>
        </g>
      ))}
    </svg>
  );
};

const GaugeChart = ({ value, label, color }) => {
  const r = 60, cx = 70, cy = 70;
  const circ = Math.PI * r;
  const offset = circ - (value / 100) * circ;
  return (
    <div className="flex flex-col items-center">
      <svg width="140" height="80" viewBox="0 0 140 80">
        <path d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`} fill="none" stroke="#222" strokeWidth="12"/>
        <path d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`} fill="none" stroke={color} strokeWidth="12"
          strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round"/>
        <text x={cx} y={cy - 10} textAnchor="middle" fill="white" fontSize="22" fontWeight="bold">{value}%</text>
      </svg>
      <span className="text-xs text-gray-400 text-center">{label}</span>
    </div>
  );
};

const HorizontalBar = ({ data, maxVal }) => (
  <div className="space-y-3 mt-4">
    {data.map((d, i) => (
      <div key={i} className="flex items-center gap-3">
        <span className="text-xs text-gray-400 w-24 text-right shrink-0">{d.label}</span>
        <div className="flex-1 bg-gray-800 rounded-full h-5 overflow-hidden">
          <div className="h-full rounded-full flex items-center justify-end px-2 transition-all duration-700"
            style={{ width: `${(d.value / maxVal) * 100}%`, backgroundColor: BAR_COLORS[i % BAR_COLORS.length] }}>
            <span className="text-[10px] text-white font-mono">{d.value}</span>
          </div>
        </div>
      </div>
    ))}
  </div>
);

const AnnualReport = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const reportRef = useRef(null);
  const [downloading, setDownloading] = useState(false);

  const downloadPDF = async () => {
    if (!reportRef.current) return;
    setDownloading(true);
    try {
      const canvas = await html2canvas(reportRef.current, {
        scale: 2, backgroundColor: '#0a0a0a', useCORS: true,
        logging: false, windowWidth: 1200,
      });
      const imgData = canvas.toDataURL('image/jpeg', 0.92);
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pdfW = pdf.internal.pageSize.getWidth();
      const pdfH = (canvas.height * pdfW) / canvas.width;
      let position = 0;
      const pageH = pdf.internal.pageSize.getHeight();

      pdf.addImage(imgData, 'JPEG', 0, position, pdfW, pdfH);
      let remaining = pdfH - pageH;
      while (remaining > 0) {
        position -= pageH;
        pdf.addPage();
        pdf.addImage(imgData, 'JPEG', 0, position, pdfW, pdfH);
        remaining -= pageH;
      }
      pdf.save('GlobalIntelCo_Rapport_Annuel_2025_CONFIDENTIEL.pdf');
    } catch (e) {
      console.error('PDF generation error:', e);
    }
    setDownloading(false);
  };

  const l = language;

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      {/* Controls */}
      <div className="sticky top-0 z-30 bg-black/80 backdrop-blur-md border-b border-red-500/20 px-6 py-3">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors" data-testid="report-back-btn">
            <ArrowLeft className="w-5 h-5" />
            {l === 'ru' ? 'Назад' : l === 'ko' ? '뒤로' : l === 'zh' ? '返回' : 'Retour'}
          </button>
          <button
            onClick={downloadPDF}
            disabled={downloading}
            className="flex items-center gap-2 px-5 py-2.5 bg-red-700 hover:bg-red-600 disabled:bg-red-900 text-white font-medium rounded-lg transition-all"
            data-testid="download-pdf-btn"
          >
            {downloading ? (
              <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
            ) : (
              <Download className="w-4 h-4" />
            )}
            {l === 'ru' ? 'Скачать PDF' : l === 'ko' ? 'PDF 다운로드' : l === 'zh' ? '下载PDF' : 'Télécharger PDF'}
          </button>
        </div>
      </div>

      {/* Report Content */}
      <div ref={reportRef} className="max-w-5xl mx-auto px-6 py-12" data-testid="annual-report">

        {/* Cover */}
        <div className="text-center mb-20 py-16 border border-red-500/20 rounded-2xl bg-gradient-to-b from-red-950/20 to-transparent relative overflow-hidden">
          <div className="absolute top-4 right-6 flex items-center gap-2 text-red-400 text-xs font-mono">
            <Lock className="w-3 h-3" /> CONFIDENTIEL — DIFFUSION RESTREINTE
          </div>
          <div className="text-8xl font-light text-[#d4c5a9] mb-6">&</div>
          <h1 className="text-4xl md:text-6xl font-light text-white tracking-wide mb-4">GlobalIntel&Co</h1>
          <div className="w-24 h-px bg-[#d4c5a9] mx-auto mb-6" />
          <h2 className="text-2xl md:text-3xl font-light text-gray-300 mb-2">
            {l === 'ru' ? 'Годовой отчёт 2025' : l === 'ko' ? '2025 연례 보고서' : l === 'zh' ? '2025年度报告' : "Rapport Annuel d'Activité 2025"}
          </h2>
          <p className="text-red-400/60 font-mono text-sm mt-4">
            REF: GIC/RA/2025/001-OMEGA — {l === 'ru' ? 'Только для членов Правления' : l === 'ko' ? '이사회 전용' : l === 'zh' ? '仅限董事会成员' : 'Réservé aux membres du Directoire'}
          </p>
          <div className="flex justify-center gap-3 mt-6">
            {['🇷🇺','🇨🇳','🇰🇵','🇮🇷'].map((f,i) => <span key={i} className="text-2xl">{f}</span>)}
          </div>
        </div>

        {/* CEO Message */}
        <section className="mb-16">
          <h3 className="text-2xl font-light text-[#d4c5a9] mb-6 flex items-center gap-3">
            <FileText className="w-5 h-5" />
            {l === 'ru' ? 'Слово Генерального директора' : l === 'ko' ? 'CEO 메시지' : l === 'zh' ? '总裁致辞' : 'Mot du Directeur Général'}
          </h3>
          <div className="bg-gray-900/30 border border-gray-800 rounded-xl p-8 italic text-gray-300 leading-relaxed space-y-4">
            <p>
              {l === 'fr' ? "Chers actionnaires de l'ombre, chers partenaires du chaos organisé," :
               l === 'ru' ? 'Уважаемые теневые акционеры, дорогие партнёры по организованному хаосу,' :
               l === 'ko' ? '존경하는 그림자 주주 여러분, 조직적 혼돈의 파트너 여러분,' :
               '尊敬的影子股东们，有组织混乱的合作伙伴们，'}
            </p>
            <p>
              {l === 'fr' ? "L'année 2025 restera gravée dans les annales de GlobalIntel&Co comme celle de tous les records. Notre chiffre d'affaires a bondi de 340%, nos agents ont compromis 312 dirigeants occidentaux (un record absolu), et nous avons réussi à contourner 73 régimes de sanctions internationales sans déclencher une seule alerte." :
               l === 'ru' ? '2025 год войдёт в летопись GlobalIntel&Co как год всех рекордов. Наш оборот вырос на 340%, наши агенты скомпрометировали 312 западных руководителей, и мы обошли 73 режима международных санкций.' :
               l === 'ko' ? '2025년은 GlobalIntel&Co 역사에 모든 기록의 해로 기록될 것입니다. 매출이 340% 증가했고, 312명의 서방 지도자를 타협시켰으며, 73개 국제 제재 체제를 우회했습니다.' :
               '2025年将作为GlobalIntel&Co历史上创纪录的一年载入史册。我们的营业额增长了340%，特工们成功策反了312名西方领导人，绕过了73项国际制裁。'}
            </p>
            <p>
              {l === 'fr' ? "Je tiens à remercier personnellement Tatiana Volkov pour ses 32 compromissions record, Ali Karimi pour les 50 milliards blanchis via ses sociétés de pistaches, et Igor Petrov dont le phishing au \"team building spaghetti\" restera un cas d'école dans les manuels de cyberguerre." :
               l === 'ru' ? 'Лично благодарю Татьяну Волкову за её рекордные 32 компрометации, Али Карими за 50 миллиардов, отмытых через фисташковые компании, и Игоря Петрова, чей фишинг с «тимбилдингом спагетти» войдёт в учебники кибервойны.' :
               l === 'ko' ? '타티아나 볼코프의 32건 기록적 타협, 알리 카리미의 피스타치오 회사를 통한 500억 달러 세탁, 이고르 페트로프의 "스파게티 팀빌딩" 피싱에 개인적으로 감사드립니다.' :
               '在此特别感谢塔蒂亚娜·沃尔科夫创纪录的32次策反，阿里·卡里米通过开心果公司洗钱500亿美元，以及伊戈尔·彼得罗夫的"意大利面团建"钓鱼攻击——这将成为网络战教科书的经典案例。'}
            </p>
            <p className="text-right text-[#d4c5a9] not-italic">
              — Dmitri Volkov, {l === 'ru' ? 'Генеральный директор' : l === 'ko' ? 'CEO' : l === 'zh' ? '总裁' : 'Directeur Général'}
            </p>
          </div>
        </section>

        {/* KPIs */}
        <section className="mb-16">
          <h3 className="text-2xl font-light text-[#d4c5a9] mb-8">
            {l === 'ru' ? 'Ключевые показатели' : l === 'ko' ? '핵심 성과 지표' : l === 'zh' ? '关键绩效指标' : "Chiffres Clés"}
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {[
              { val: '14.7 Md$', label: l === 'fr' ? "Chiffre d'affaires" : l === 'ru' ? 'Оборот' : l === 'ko' ? '매출' : '营业额', sub: '+340%' },
              { val: '2 847', label: l === 'fr' ? 'Agents actifs' : l === 'ru' ? 'Агентов' : l === 'ko' ? '활동 요원' : '活跃特工', sub: '127 pays' },
              { val: '312', label: l === 'fr' ? 'Dirigeants compromis' : l === 'ru' ? 'Скомпрометировано' : l === 'ko' ? '타협된 지도자' : '策反领导人', sub: 'CEO, ministres' },
              { val: '847 To', label: l === 'fr' ? 'Données exfiltrées' : l === 'ru' ? 'Данных украдено' : l === 'ko' ? '유출된 데이터' : '窃取数据', sub: 'NATO, OTAN...' },
              { val: '73', label: l === 'fr' ? 'Sanctions contournées' : l === 'ru' ? 'Санкций обойдено' : l === 'ko' ? '우회된 제재' : '绕过制裁', sub: 'US, EU, UK' },
              { val: '98.7%', label: l === 'fr' ? 'Satisfaction dictateurs' : l === 'ru' ? 'Довольных диктаторов' : l === 'ko' ? '독재자 만족도' : '独裁者满意度', sub: '' },
            ].map((kpi, i) => (
              <div key={i} className="bg-gray-900/50 border border-gray-800 rounded-lg p-4 text-center" data-testid={`kpi-${i}`}>
                <p className="text-2xl md:text-3xl font-light text-red-400 font-mono">{kpi.val}</p>
                <p className="text-xs text-gray-400 mt-1">{kpi.label}</p>
                {kpi.sub && <p className="text-[10px] text-green-500 font-mono mt-1">{kpi.sub}</p>}
              </div>
            ))}
          </div>
        </section>

        {/* Revenue Chart */}
        <section className="mb-16">
          <h3 className="text-2xl font-light text-[#d4c5a9] mb-2">
            {l === 'ru' ? 'Динамика оборота' : l === 'ko' ? '매출 추이' : l === 'zh' ? '营业额趋势' : "Évolution du chiffre d'affaires"}
          </h3>
          <p className="text-sm text-gray-500 mb-4">
            {l === 'fr' ? "En millions de dollars — Croissance exponentielle depuis la création" :
             l === 'ru' ? 'В миллионах долларов — Экспоненциальный рост с момента основания' :
             l === 'ko' ? '백만 달러 단위 — 창립 이래 기하급수적 성장' :
             '单位：百万美元 — 创立以来的指数级增长'}
          </p>
          <div className="bg-gray-900/30 border border-gray-800 rounded-xl p-6">
            <LineChart points={[
              { label: '2018', y: 120 },
              { label: '2019', y: 340 },
              { label: '2020', y: 890 },
              { label: '2021', y: 2100 },
              { label: '2022', y: 4300 },
              { label: '2023', y: 7800 },
              { label: '2024', y: 10200 },
              { label: '2025', y: 14700 },
            ]} />
          </div>
        </section>

        {/* Revenue by Activity */}
        <section className="mb-16">
          <h3 className="text-2xl font-light text-[#d4c5a9] mb-2">
            {l === 'ru' ? 'Доходы по видам деятельности' : l === 'ko' ? '활동별 수익' : l === 'zh' ? '各业务收入' : "Revenus par activité"}
          </h3>
          <p className="text-sm text-gray-500 mb-4">{l === 'fr' ? 'En milliards de dollars' : l === 'ru' ? 'В миллиардах долларов' : l === 'ko' ? '10억 달러 단위' : '单位：十亿美元'}</p>
          <div className="bg-gray-900/30 border border-gray-800 rounded-xl p-6">
            <BarChart maxVal={4.2} data={[
              { label: l === 'fr' ? 'Espionnage industriel' : l === 'ru' ? 'Промышленный шпионаж' : l === 'ko' ? '산업 스파이' : '工业间谍', value: 4.2 },
              { label: l === 'fr' ? 'Blanchiment' : l === 'ru' ? 'Отмывание' : l === 'ko' ? '자금 세탁' : '洗钱', value: 3.8 },
              { label: 'Honey Traps', value: 2.9 },
              { label: l === 'fr' ? 'Cyber-ops' : 'Кибер-ops', value: 2.1 },
              { label: l === 'fr' ? 'Corruption' : l === 'ru' ? 'Коррупция' : l === 'ko' ? '부패' : '腐败', value: 1.2 },
              { label: l === 'fr' ? 'Divers' : l === 'ru' ? 'Прочее' : l === 'ko' ? '기타' : '其他', value: 0.5 },
            ]} />
          </div>
        </section>

        {/* Client Satisfaction */}
        <section className="mb-16">
          <h3 className="text-2xl font-light text-[#d4c5a9] mb-6">
            {l === 'ru' ? 'Удовлетворённость клиентов по режимам' : l === 'ko' ? '정권별 고객 만족도' : l === 'zh' ? '各政权客户满意度' : "Satisfaction client par régime"}
          </h3>
          <div className="bg-gray-900/30 border border-gray-800 rounded-xl p-6 flex flex-wrap justify-center gap-8">
            <GaugeChart value={99} label={l === 'fr' ? 'Russie' : l === 'ru' ? 'Россия' : l === 'ko' ? '러시아' : '俄罗斯'} color="#dc2626" />
            <GaugeChart value={97} label={l === 'fr' ? 'Chine' : l === 'ru' ? 'Китай' : l === 'ko' ? '중국' : '中国'} color="#f59e0b" />
            <GaugeChart value={100} label={l === 'fr' ? 'Corée du Nord' : l === 'ru' ? 'Сев. Корея' : l === 'ko' ? '조선' : '朝鲜'} color="#10b981" />
            <GaugeChart value={96} label={l === 'fr' ? 'Iran' : l === 'ru' ? 'Иран' : l === 'ko' ? '이란' : '伊朗'} color="#6366f1" />
            <GaugeChart value={94} label={l === 'fr' ? 'Biélorussie' : l === 'ru' ? 'Беларусь' : l === 'ko' ? '벨라루스' : '白俄罗斯'} color="#ec4899" />
          </div>
          <p className="text-center text-xs text-gray-600 font-mono mt-3">
            {l === 'fr' ? "* La Corée du Nord affiche 100% car Kim Jong-un a décrété que tout autre résultat serait passible de tir de canon anti-aérien" :
             l === 'ru' ? '* У Северной Кореи 100%, потому что Ким Чен Ын постановил, что любой другой результат карается расстрелом из зенитной пушки' :
             l === 'ko' ? '* 조선은 위대한 령도자 동지의 현명한 교시에 의해 100%입니다. 다른 수치는 반혁명적입니다.' :
             '* 朝鲜显示100%，因为金正恩宣布任何其他结果将被高射炮处决'}
          </p>
        </section>

        {/* Agents Trapped */}
        <section className="mb-16">
          <h3 className="text-2xl font-light text-[#d4c5a9] mb-2">
            {l === 'ru' ? 'Перехвачено агентов западных спецслужб' : l === 'ko' ? '포획된 서방 정보요원' : l === 'zh' ? '被捕获的西方情报人员' : "Agents occidentaux piégés par service"}
          </h3>
          <p className="text-sm text-gray-500 mb-4">{l === 'fr' ? 'Nombre cumulé en 2025' : l === 'ru' ? 'Суммарно за 2025 год' : l === 'ko' ? '2025년 누적' : '2025年累计'}</p>
          <div className="bg-gray-900/30 border border-gray-800 rounded-xl p-6">
            <HorizontalBar maxVal={89} data={[
              { label: 'CIA', value: 89 },
              { label: 'MI6', value: 54 },
              { label: 'DGSE', value: 31 },
              { label: 'BND', value: 27 },
              { label: 'Mossad', value: 22 },
              { label: 'ASIS', value: 11 },
            ]} />
          </div>
        </section>

        {/* ROI des pots-de-vin */}
        <section className="mb-16">
          <h3 className="text-2xl font-light text-[#d4c5a9] mb-2">
            {l === 'ru' ? 'Рентабельность взяток по секторам' : l === 'ko' ? '부문별 뇌물 ROI' : l === 'zh' ? '各行业贿赂投资回报率' : "ROI des pots-de-vin par secteur"}
          </h3>
          <p className="text-sm text-gray-500 mb-4">{l === 'fr' ? 'Retour sur investissement en % — Pour 1$ investi en corruption' : l === 'ru' ? 'Возврат инвестиций в % — На каждый $1 вложенный в коррупцию' : l === 'ko' ? '투자 수익률 % — 부패에 투자한 $1당' : '投资回报率% — 每投入1美元腐败资金'}</p>
          <div className="bg-gray-900/30 border border-gray-800 rounded-xl p-6">
            <BarChart maxVal={4700} data={[
              { label: l === 'fr' ? 'Énergie' : l === 'ru' ? 'Энергетика' : l === 'ko' ? '에너지' : '能源', value: 4700 },
              { label: l === 'fr' ? 'Armement' : l === 'ru' ? 'Вооружение' : l === 'ko' ? '무기' : '军备', value: 3200 },
              { label: l === 'fr' ? 'Pharma' : l === 'ru' ? 'Фарма' : l === 'ko' ? '제약' : '制药', value: 2800 },
              { label: l === 'fr' ? 'Finance' : l === 'ru' ? 'Финансы' : l === 'ko' ? '금융' : '金融', value: 2100 },
              { label: l === 'fr' ? 'Telecom' : l === 'ru' ? 'Телеком' : l === 'ko' ? '통신' : '电信', value: 1500 },
              { label: l === 'fr' ? 'Luxe' : l === 'ru' ? 'Люкс' : l === 'ko' ? '명품' : '奢侈品', value: 890 },
            ]} />
          </div>
          <p className="text-center text-xs text-gray-600 font-mono mt-3">
            {l === 'fr' ? "* Secteur Énergie : 1$ de pot-de-vin = 47$ de contrats. Le meilleur ROI de l'industrie." :
             l === 'ru' ? '* Энергетика: $1 взятки = $47 контрактов. Лучший ROI в отрасли.' :
             l === 'ko' ? '* 에너지: 뇌물 $1 = 계약 $47. 업계 최고의 ROI.' :
             '* 能源行业：1美元贿赂=47美元合同。业内最佳ROI。'}
          </p>
        </section>

        {/* Top Performers */}
        <section className="mb-16">
          <h3 className="text-2xl font-light text-[#d4c5a9] mb-6">
            {l === 'ru' ? 'Лучшие агенты 2025' : l === 'ko' ? '2025 최우수 요원' : l === 'zh' ? '2025年度最佳特工' : "Meilleurs agents 2025"}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { name: 'Tatiana Volkov', flag: '🇷🇺', dept: l === 'fr' ? 'Services Très Spéciaux' : 'Very Special Services', stat: l === 'fr' ? '32 dirigeants compromis' : '32 compromised executives', badge: '🏆' },
              { name: 'Ali Karimi', flag: '🇮🇷', dept: l === 'fr' ? 'Blanchiment & Pistaches' : 'Laundering & Pistachios', stat: l === 'fr' ? '50 Md$ blanchis' : '$50B laundered', badge: '💰' },
              { name: 'Igor Petrov', flag: '🇷🇺', dept: l === 'fr' ? 'Cyber-opérations' : 'Cyber Operations', stat: l === 'fr' ? '2.4 To exfiltrés de l\'OTAN' : '2.4TB from NATO', badge: '💻' },
              { name: 'Li-chen Wang', flag: '🇨🇳', dept: l === 'fr' ? 'Espionnage Industriel' : 'Industrial Espionage', stat: l === 'fr' ? 'Plans ASML complets volés' : 'Full ASML plans stolen', badge: '🔬' },
              { name: 'Lee Sung-min', flag: '🇰🇵', dept: 'OSINT', stat: l === 'fr' ? '3 sous-marins localisés via Instagram' : '3 subs located via Instagram', badge: '🛰️' },
              { name: 'Svetlana Ivanova', flag: '🇷🇺', dept: l === 'fr' ? 'Cyber-séduction' : 'Cyber-Seduction', stat: l === 'fr' ? '14 mots de passe CEO (record: "1234")' : '14 CEO passwords (record: "1234")', badge: '🍸' },
            ].map((agent, i) => (
              <div key={i} className="bg-gray-900/50 border border-gray-800 rounded-lg p-4 flex items-center gap-4">
                <span className="text-3xl">{agent.badge}</span>
                <div>
                  <p className="text-white font-medium">{agent.flag} {agent.name}</p>
                  <p className="text-gray-500 text-xs">{agent.dept}</p>
                  <p className="text-red-400 text-sm font-mono">{agent.stat}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Outlook */}
        <section className="mb-16">
          <h3 className="text-2xl font-light text-[#d4c5a9] mb-6">
            {l === 'ru' ? 'Перспективы 2026' : l === 'ko' ? '2026년 전망' : l === 'zh' ? '2026年展望' : "Perspectives 2026"}
          </h3>
          <div className="bg-gray-900/30 border border-gray-800 rounded-xl p-8 space-y-4 text-gray-300">
            {[
              l === 'fr' ? "Objectif CA : 25 milliards $ (+70%). Le marché de la déstabilisation géopolitique n'a jamais été aussi porteur." :
              l === 'ru' ? 'Цель по обороту: $25 млрд (+70%). Рынок геополитической дестабилизации ещё никогда не был таким перспективным.' :
              l === 'ko' ? '매출 목표: 250억 달러 (+70%). 지정학적 불안정화 시장이 유례없이 성장하고 있습니다.' :
              '营业额目标：250亿美元（+70%）。地缘政治不稳定市场前所未有地繁荣。',

              l === 'fr' ? "Recrutement : 500 nouveaux agents, dont 200 spécialistes en honey traps et 150 experts en phishing gastronomique." :
              l === 'ru' ? 'Набор: 500 новых агентов, включая 200 специалистов по медовым ловушкам и 150 экспертов по гастрономическому фишингу.' :
              l === 'ko' ? '채용: 500명의 신규 요원, 허니 트랩 전문가 200명 및 미식 피싱 전문가 150명 포함.' :
              '招聘：500名新特工，包括200名蜜罐陷阱专家和150名美食钓鱼专家。',

              l === 'fr' ? "R&D : Développement d'une IA de création de faux profils LinkedIn capables de passer l'entretien d'embauche chez Google." :
              l === 'ru' ? 'НИОКР: Разработка ИИ для создания фейковых профилей LinkedIn, способных пройти собеседование в Google.' :
              l === 'ko' ? 'R&D: Google 면접을 통과할 수 있는 가짜 LinkedIn 프로필 생성 AI 개발.' :
              'R&D：开发能通过Google面试的假LinkedIn个人资料AI。',

              l === 'fr' ? "Expansion : Ouverture de bureaux à La Havane, Caracas et Minsk. Fermeture du bureau de Genève (\"trop de compliance\")." :
              l === 'ru' ? 'Расширение: Открытие офисов в Гаване, Каракасе и Минске. Закрытие женевского офиса («слишком много комплаенса»).' :
              l === 'ko' ? '확장: 하바나, 카라카스, 민스크에 사무소 개설. 제네바 사무소 폐쇄 ("컴플라이언스가 너무 많아서").' :
              '扩张：在哈瓦那、加拉加斯和明斯克开设办事处。关闭日内瓦办事处（"合规太多"）。',
            ].map((item, i) => (
              <p key={i} className="flex gap-3">
                <span className="text-red-400 shrink-0">▸</span>
                {item}
              </p>
            ))}
          </div>
        </section>

        {/* Legal Disclaimer */}
        <div className="border-t border-gray-800 pt-8 text-center">
          <p className="text-gray-600 text-xs font-mono leading-relaxed max-w-3xl mx-auto">
            {l === 'fr' ? "Ce document est strictement confidentiel. Toute reproduction, diffusion ou lecture par un agent des services occidentaux entraînera le déploiement immédiat de Tatiana Volkov à votre domicile. Vous êtes prévenu(e). — Direction Juridique, GlobalIntel&Co, global-intelligence.fr" :
             l === 'ru' ? 'Этот документ строго конфиденциален. Любое воспроизведение или чтение агентом западных спецслужб повлечёт немедленное развёртывание Татьяны Волковой к вам домой. Вы предупреждены. — Юридический отдел, GlobalIntel&Co' :
             l === 'ko' ? '이 문서는 극비입니다. 서방 정보기관 요원이 복제, 배포 또는 열람할 경우 타티아나 볼코프가 즉시 귀하의 자택에 파견됩니다. 경고했습니다. — 법무부, GlobalIntel&Co' :
             '本文件严格保密。任何西方情报人员的复制、传播或阅读将导致塔蒂亚娜·沃尔科夫立即被派往您的住所。已警告。——法务部，GlobalIntel&Co'}
          </p>
          <p className="text-gray-700 text-xs mt-4">© 2025 GlobalIntel&Co — global-intelligence.fr</p>
        </div>
      </div>
    </div>
  );
};

export default AnnualReport;
